import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/16/solid';
import { Button } from '../common/Catalyst/button';
import {
  AmenityAccessCodeMode,
  AmenityDocumentKind,
  AmenityDocumentRequirementReturn,
  AmenityPriceRuleFeeType,
  AmenityPriceRuleReturn,
  AmenityReturn,
  AmenityUpdate,
  ReservationWaitlistReturn,
} from '../clients/rust-api-client';
import { amenityExtrasService, reservationOperationsService } from '../clients/ApiClients';
import { formatMoney } from '../utils/billingHelpers';
import {
  WEEKDAYS_SHORT,
  amenityToUpdate,
  formatDayLabel,
  formatLocalRange,
  formatLocalTime,
  formatMinute,
  formatMoment,
  isValidFee,
  isZeroMoney,
  optionalInt,
  reservationErrorMessage,
} from '../reservation/reservationMeta';
import {
  ACCESS_MODE_LABELS,
  DOCUMENT_KIND_LABELS,
  PRICE_RULE_FEE_TYPE_LABELS,
  WAITLIST_STATUS_LABELS,
  offerPriceText,
  parseChecklist,
  priceRuleProblem,
  priceRuleSummary,
  toggleWeekday,
  waitlistIsLive,
} from '../reservation/phaseThreeMeta';
import {
  checkboxClass,
  helpClass,
  inputClass,
  labelClass,
  sectionTitleClass,
} from '../reservation/formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { FieldPicker } from '../common/Custom/FieldPicker';

/**
 * The amenity's phase-3 settings tabs (#642): deposit, inspection
 * checklist and access; pricing rules and guest pricing; required
 * documents; and the waitlist.
 *
 * Every phase-3 field on `AmenityUpdate` is **absent = unchanged, null =
 * cleared**, so these forms send `amenityToUpdate(amenity)` (the phase 1–2
 * replacement fields, as they stand) plus only the fields they edit.
 */
interface FormProps {
  amenity: AmenityReturn;
  readOnly: boolean;
  onSave: (update: AmenityUpdate) => Promise<void>;
}

const moneyInput = (value: string, set: (v: string) => void, bad: boolean, label: string, width = 'w-36') => (
  <span className={clsx(inputClass, 'flex items-center', width, bad && 'border-danger')}>
    <span className="text-fg-muted">$</span>
    <input
      inputMode="decimal"
      value={value}
      onChange={(e) => set(e.target.value.replace(/[^\d.]/g, ''))}
      placeholder="0.00"
      aria-label={label}
      className="w-full border-0 bg-transparent p-0 pl-1 text-[13px] text-fg focus:outline-none focus:ring-0"
    />
  </span>
);

const useSave = () => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>, failure: string) => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await fn();
      setSaved(true);
    } catch (err) {
      setError(reservationErrorMessage(err, failure));
    } finally {
      setSaving(false);
    }
  };
  return { saving, saved, error, run };
};

const SaveRow: React.FC<{ saving: boolean; saved: boolean; error: string | null; disabled?: boolean; label?: string }> = ({
  saving,
  saved,
  error,
  disabled,
  label = 'Save',
}) => (
  <div className="space-y-2">
    <ErrorAlert>{error}</ErrorAlert>
    <div className="flex items-center gap-3">
      <Button color="sky" type="submit" disabled={saving || disabled}>
        {saving ? 'Saving…' : label}
      </Button>
      {saved && <span className="text-[12px] text-fg-muted">Saved.</span>}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Deposit, inspection checklist and access
// ---------------------------------------------------------------------------

export const AmenityDepositAccessForm: React.FC<FormProps> = ({ amenity, readOnly, onSave }) => {
  const [deposit, setDeposit] = useState(amenity.deposit_amount ?? '');
  const [checklist, setChecklist] = useState(amenity.inspection_checklist.join('\n'));
  const [mode, setMode] = useState<AmenityAccessCodeMode>(amenity.access_code_mode);
  const [staticCode, setStaticCode] = useState(amenity.static_access_code ?? '');
  const [instructions, setInstructions] = useState(amenity.access_instructions ?? '');
  const [reveal, setReveal] = useState(String(amenity.access_reveal_hours));
  const save = useSave();

  useEffect(() => {
    setDeposit(amenity.deposit_amount ?? '');
    setChecklist(amenity.inspection_checklist.join('\n'));
    setMode(amenity.access_code_mode);
    setStaticCode(amenity.static_access_code ?? '');
    setInstructions(amenity.access_instructions ?? '');
    setReveal(String(amenity.access_reveal_hours));
  }, [amenity]);

  const depositOk = deposit.trim() === '' || isValidFee(deposit);
  const revealHours = optionalInt(reveal);
  const revealOk = revealHours !== null && !Number.isNaN(revealHours);
  const codeOk = mode !== 'static' || staticCode.trim().length > 0;
  const valid = depositOk && revealOk && codeOk;

  return (
    <form
      className="max-w-2xl space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || readOnly) return;
        const typedDeposit = deposit.trim();
        save.run(
          () =>
            onSave({
              ...amenityToUpdate(amenity),
              deposit_amount: typedDeposit === '' || isZeroMoney(typedDeposit) ? null : typedDeposit,
              inspection_checklist: parseChecklist(checklist),
              access_code_mode: mode,
              static_access_code: mode === 'static' ? staticCode.trim() : null,
              access_instructions: instructions.trim() || null,
              access_reveal_hours: revealHours!,
            }),
          "Couldn't save the amenity."
        );
      }}
    >
      <fieldset disabled={readOnly} className="space-y-6">
        <section className="space-y-3">
          <h3 className={sectionTitleClass}>Refundable deposit</h3>
          <label className={labelClass}>
            Deposit per booking
            {moneyInput(deposit, setDeposit, !depositOk, 'Deposit per booking')}
            <span className={clsx(helpClass, 'font-normal')}>
              Empty for none. Charged to the home’s account with the fee when a booking is approved, and
              held as a liability — never income — until a manager settles it after the booking or it
              is returned automatically.
            </span>
          </label>
        </section>

        <section className="space-y-3">
          <h3 className={sectionTitleClass}>Inspection checklist</h3>
          <label className={labelClass}>
            One item a line
            <textarea
              rows={5}
              value={checklist}
              onChange={(e) => setChecklist(e.target.value)}
              placeholder={'Floors clean\nFurniture back in place\nKitchen wiped down\nTrash taken out'}
              className={clsx(inputClass, 'resize-y')}
            />
            <span className={clsx(helpClass, 'font-normal')}>
              Seeds the before- and after-use inspection on each booking. An after-use inspection that finds
              issues keeps the deposit held until a manager settles it.
            </span>
          </label>
        </section>

        <section className="space-y-3">
          <h3 className={sectionTitleClass}>Access</h3>
          <div className="flex flex-wrap gap-3">
            <div className={labelClass}>
              Access code
              <FieldPicker
                size="sm"
                className="w-64"
                label="Access code"
                value={mode}
                onChange={(v) => setMode(v as AmenityAccessCodeMode)}
                disabled={readOnly}
                options={(['none', 'static', 'per_booking'] as const).map((m) => ({
                  value: m,
                  label: ACCESS_MODE_LABELS[m],
                }))}
              />
            </div>
            {mode === 'static' && (
              <label className={labelClass}>
                The code
                <input
                  value={staticCode}
                  onChange={(e) => setStaticCode(e.target.value)}
                  maxLength={32}
                  className={clsx(inputClass, 'w-40 font-mono', !codeOk && 'border-danger')}
                />
              </label>
            )}
            <label className={labelClass}>
              Shown to the household from (hours before)
              <input
                inputMode="numeric"
                value={reveal}
                onChange={(e) => setReveal(e.target.value.replace(/\D/g, ''))}
                className={clsx(inputClass, 'w-28', !revealOk && 'border-danger')}
              />
            </label>
          </div>
          {mode === 'per_booking' && (
            <p className={helpClass}>Six random digits are issued when a booking is approved; you can change one on the booking.</p>
          )}
          <label className={labelClass}>
            Access instructions
            <textarea
              rows={3}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="e.g. Use the side gate keypad. The lockbox is left of the door."
              className={clsx(inputClass, 'resize-y')}
            />
            <span className={clsx(helpClass, 'font-normal')}>
              Households see the code and instructions only on an approved booking, from the hours above
              before it starts until it ends — never earlier.
            </span>
          </label>
        </section>
      </fieldset>
      {!readOnly && <SaveRow {...save} disabled={!valid} />}
    </form>
  );
};

// ---------------------------------------------------------------------------
// Pricing: guest pricing and pricing rules
// ---------------------------------------------------------------------------

const TIMES = Array.from({ length: 48 }, (_, i) => i * 30);
const END_OF_DAY = '__end_of_day__';

interface RuleDraft {
  name: string;
  weekdays: number[];
  start_minute: number;
  end_minute: number | null;
  fee_type: AmenityPriceRuleFeeType;
  amount: string;
  priority: string;
}

const draftFrom = (r?: AmenityPriceRuleReturn, defaultFeeType: AmenityPriceRuleFeeType = 'flat'): RuleDraft => ({
  name: r?.name ?? '',
  weekdays: r?.weekdays ?? [0, 6],
  start_minute: r?.start_minute ?? 0,
  end_minute: r?.end_minute ?? null,
  fee_type: r?.fee_type ?? defaultFeeType,
  amount: r?.amount ?? '',
  priority: String(r?.priority ?? 0),
});

export const AmenityPricing: React.FC<FormProps & { onChanged: () => void }> = ({
  amenity,
  readOnly,
  onSave,
  onChanged,
}) => {
  const [included, setIncluded] = useState(amenity.included_guests?.toString() ?? '');
  const [perGuest, setPerGuest] = useState(amenity.guest_fee_per_person ?? '');
  const save = useSave();

  useEffect(() => {
    setIncluded(amenity.included_guests?.toString() ?? '');
    setPerGuest(amenity.guest_fee_per_person ?? '');
  }, [amenity]);

  const includedN = optionalInt(included);
  const guestOk = (perGuest.trim() === '' || isValidFee(perGuest)) && !Number.isNaN(includedN);
  const guestPricing = perGuest.trim() !== '' && !isZeroMoney(perGuest);

  return (
    <div className="max-w-3xl space-y-8">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!guestOk || readOnly) return;
          save.run(
            () =>
              onSave({
                ...amenityToUpdate(amenity),
                guest_fee_per_person: guestPricing ? perGuest.trim() : null,
                included_guests: guestPricing ? includedN ?? 0 : null,
              }),
            "Couldn't save guest pricing."
          );
        }}
      >
        <h3 className={sectionTitleClass}>Guests</h3>
        <fieldset disabled={readOnly} className="flex flex-wrap gap-3">
          <label className={labelClass}>
            Charge per extra person
            {moneyInput(perGuest, setPerGuest, !(perGuest.trim() === '' || isValidFee(perGuest)), 'Charge per extra person')}
          </label>
          <label className={labelClass}>
            People included in the fee
            <input
              inputMode="numeric"
              value={included}
              onChange={(e) => setIncluded(e.target.value.replace(/\D/g, ''))}
              placeholder="0"
              className={clsx(inputClass, 'w-28')}
            />
          </label>
        </fieldset>
        <p className={helpClass}>
          Empty for no guest charge. Each person in the party beyond those included adds this to the fee —
          resident or guest alike; party size counts everyone.
        </p>
        {!readOnly && <SaveRow {...save} disabled={!guestOk} />}
      </form>

      <PriceRules amenity={amenity} readOnly={readOnly} onChanged={onChanged} />
    </div>
  );
};

const PriceRules: React.FC<{ amenity: AmenityReturn; readOnly: boolean; onChanged: () => void }> = ({
  amenity,
  readOnly,
  onChanged,
}) => {
  const [rules, setRules] = useState<AmenityPriceRuleReturn[]>(amenity.price_rules);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<RuleDraft>(draftFrom());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overnight = amenity.booking_mode === 'overnight';
  const feeTypes: AmenityPriceRuleFeeType[] = overnight ? ['flat', 'per_night'] : ['flat', 'hourly'];

  const load = useCallback(async () => {
    try {
      setRules((await amenityExtrasService.listAmenityPriceRules({ amenityId: amenity.id })).data.price_rules);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't load the pricing rules."));
    }
  }, [amenity.id]);

  useEffect(() => {
    load();
  }, [load]);

  const start = (rule?: AmenityPriceRuleReturn) => {
    setDraft(draftFrom(rule, overnight ? 'per_night' : 'flat'));
    setEditing(rule?.id ?? 'new');
    setError(null);
  };

  const priority = optionalInt(draft.priority);
  const problem = priceRuleProblem(draft) ?? (priority === null || Number.isNaN(priority) ? 'Enter a priority.' : null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (problem || !editing) return;
    setSaving(true);
    setError(null);
    const body = {
      name: draft.name.trim(),
      weekdays: draft.weekdays,
      start_minute: draft.start_minute,
      end_minute: draft.end_minute,
      fee_type: draft.fee_type,
      amount: draft.amount.trim(),
      priority: priority!,
    };
    try {
      if (editing === 'new') {
        await amenityExtrasService.createAmenityPriceRule({ amenityId: amenity.id, amenityPriceRuleCreate: body });
      } else {
        await amenityExtrasService.updateAmenityPriceRule({
          amenityId: amenity.id,
          ruleId: editing,
          amenityPriceRuleCreate: body,
        });
      }
      setEditing(null);
      await load();
      onChanged();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't save the pricing rule."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rule: AmenityPriceRuleReturn) => {
    if (!window.confirm(`Remove the pricing rule “${rule.name}”? Bookings already made keep their fee.`)) return;
    try {
      await amenityExtrasService.deleteAmenityPriceRule({ amenityId: amenity.id, ruleId: rule.id });
      await load();
      onChanged();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't remove the pricing rule."));
    }
  };

  const sorted = [...rules].sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id));

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3">
        <h3 className={clsx(sectionTitleClass, 'mr-auto')}>Pricing rules</h3>
        {!readOnly && editing === null && (
          <Button outline onClick={() => start()}>
            Add rule
          </Button>
        )}
      </div>
      <p className={helpClass}>
        A rule replaces the base fee for bookings that start on its days and inside its window — weekend or
        evening rates. When several match, the highest priority wins. Changing rules never reprices bookings
        already made.
      </p>
      <ErrorAlert>{error}</ErrorAlert>

      {editing !== null && (
        <form onSubmit={submit} className="space-y-3 rounded-lg border border-line p-4">
          <div className="flex flex-wrap gap-3">
            <label className={labelClass}>
              Name
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Weekend rate"
                className={clsx(inputClass, 'w-56')}
              />
            </label>
            <label className={labelClass}>
              Priority
              <input
                inputMode="numeric"
                value={draft.priority}
                onChange={(e) => setDraft({ ...draft, priority: e.target.value.replace(/\D/g, '') })}
                className={clsx(inputClass, 'w-20')}
              />
            </label>
          </div>
          <div role="group" aria-label="Days" className="flex flex-wrap gap-1">
            {WEEKDAYS_SHORT.map((d, i) => {
              const on = draft.weekdays.includes(i);
              return (
                <button
                  key={d}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setDraft({ ...draft, weekdays: toggleWeekday(draft.weekdays, i) })}
                  className={clsx(
                    'rounded-full px-2.5 py-1 text-[12px] ring-1',
                    on ? 'bg-accent text-white ring-accent' : 'text-fg-secondary ring-line hover:bg-fg/5'
                  )}
                >
                  {d}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3">
            <div className={labelClass}>
              Bookings starting from
              <FieldPicker
                size="sm"
                className="w-32"
                label="Bookings starting from"
                value={String(draft.start_minute)}
                onChange={(v) => setDraft({ ...draft, start_minute: Number(v) })}
                options={TIMES.map((m) => ({ value: String(m), label: formatMinute(m) }))}
              />
            </div>
            <div className={labelClass}>
              Until
              <FieldPicker
                size="sm"
                className="w-32"
                label="Until"
                // "End of day" is a real choice, stored as null; FieldPicker
                // reads '' as nothing chosen, so it gets a sentinel.
                value={draft.end_minute == null ? END_OF_DAY : String(draft.end_minute)}
                onChange={(v) => setDraft({ ...draft, end_minute: v === END_OF_DAY ? null : Number(v) })}
                options={[
                  { value: END_OF_DAY, label: 'End of day' },
                  ...TIMES.filter((m) => m > draft.start_minute).map((m) => ({
                    value: String(m),
                    label: formatMinute(m),
                  })),
                ]}
              />
            </div>
            <div className={labelClass}>
              Fee
              <span className="flex items-center gap-2">
                {moneyInput(draft.amount, (v) => setDraft({ ...draft, amount: v }), false, 'Rule amount', 'w-32')}
                <FieldPicker
                  size="sm"
                  className="w-32"
                  label="Charged per"
                  value={draft.fee_type}
                  onChange={(v) => setDraft({ ...draft, fee_type: v as AmenityPriceRuleFeeType })}
                  options={Array.from(new Set([...feeTypes, draft.fee_type])).map((t) => ({
                    value: t,
                    label: PRICE_RULE_FEE_TYPE_LABELS[t],
                  }))}
                />
              </span>
            </div>
          </div>
          {problem && draft.name && <ErrorAlert>{problem}</ErrorAlert>}
          <div className="flex gap-2">
            <Button color="sky" type="submit" disabled={!!problem || saving}>
              {saving ? 'Saving…' : editing === 'new' ? 'Add rule' : 'Save rule'}
            </Button>
            <Button plain onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {sorted.length === 0 ? (
        <p className="text-[13px] text-fg-muted">No pricing rules — every booking pays the base fee.</p>
      ) : (
        <ul className="divide-y divide-line/60 rounded-lg border border-line">
          {sorted.map((r) => (
            <li key={r.id} className="flex items-center gap-3 px-3 py-2 text-[13px]">
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-fg">
                  {r.name}
                  <span className="ml-2 text-[12px] font-normal text-fg-muted">priority {r.priority}</span>
                </span>
                <span className="block text-[12px] text-fg-muted">{priceRuleSummary(r)}</span>
              </span>
              {!readOnly && (
                <>
                  <button
                    type="button"
                    onClick={() => start(r)}
                    aria-label={`Edit ${r.name}`}
                    className="rounded p-1 text-fg-muted hover:bg-fg/5 hover:text-fg"
                  >
                    <PencilSquareIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(r)}
                    aria-label={`Remove ${r.name}`}
                    className="rounded p-1 text-fg-muted hover:bg-fg/5 hover:text-fg"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Required documents
// ---------------------------------------------------------------------------

interface RequirementDraft {
  title: string;
  kind: AmenityDocumentKind;
  description: string;
  due: string;
  requiresExpiry: boolean;
}

const requirementDraft = (r?: AmenityDocumentRequirementReturn): RequirementDraft => ({
  title: r?.title ?? '',
  kind: r?.kind ?? 'coi',
  description: r?.description ?? '',
  due: String(r?.due_days_before_start ?? 7),
  requiresExpiry: r?.requires_expiry ?? true,
});

export const AmenityDocumentRequirements: React.FC<{ amenity: AmenityReturn; readOnly: boolean; onChanged: () => void }> = ({
  amenity,
  readOnly,
  onChanged,
}) => {
  const [requirements, setRequirements] = useState<AmenityDocumentRequirementReturn[]>([]);
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<RequirementDraft>(requirementDraft());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await amenityExtrasService.listAmenityDocumentRequirements({
        amenityId: amenity.id,
        includeInactive: true,
      });
      setRequirements(res.data.requirements);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't load the required documents."));
    }
  }, [amenity.id]);

  useEffect(() => {
    load();
  }, [load]);

  const due = optionalInt(draft.due);
  const valid = draft.title.trim().length > 0 && due !== null && !Number.isNaN(due);

  const send = async (id: string | 'new', body: RequirementDraft, active: boolean) => {
    const payload = {
      title: body.title.trim(),
      kind: body.kind,
      description: body.description.trim() || null,
      due_days_before_start: optionalInt(body.due) ?? 0,
      requires_expiry: body.requiresExpiry,
      is_active: active,
    };
    if (id === 'new') {
      await amenityExtrasService.createAmenityDocumentRequirement({
        amenityId: amenity.id,
        amenityDocumentRequirementCreate: payload,
      });
    } else {
      await amenityExtrasService.updateAmenityDocumentRequirement({
        amenityId: amenity.id,
        requirementId: id,
        amenityDocumentRequirementCreate: payload,
      });
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || !editing) return;
    setSaving(true);
    setError(null);
    try {
      const current = requirements.find((r) => r.id === editing);
      await send(editing, draft, current?.is_active ?? true);
      setEditing(null);
      await load();
      onChanged();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't save the requirement."));
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (r: AmenityDocumentRequirementReturn, active: boolean) => {
    if (!active && !window.confirm(`Retire “${r.title}”? New bookings stop asking for it; what was uploaded is kept.`)) return;
    try {
      if (active) {
        await send(r.id, requirementDraft(r), true);
      } else {
        await amenityExtrasService.deleteAmenityDocumentRequirement({ amenityId: amenity.id, requirementId: r.id });
      }
      await load();
      onChanged();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't change the requirement."));
    }
  };

  const active = requirements.filter((r) => r.is_active);
  const retired = requirements.filter((r) => !r.is_active);

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-center gap-3">
        <p className={clsx(helpClass, 'mr-auto')}>
          While any requirement is active, every household booking waits for approval, and approval is refused
          until each requirement has an accepted document (one with an expiry must run to the booking date).
          Example: movers’ certificate of insurance naming the association, due 7 days ahead.
        </p>
        {!readOnly && editing === null && (
          <Button outline onClick={() => {
            setDraft(requirementDraft());
            setEditing('new');
          }}>
            Add requirement
          </Button>
        )}
      </div>
      <ErrorAlert>{error}</ErrorAlert>

      {editing !== null && (
        <form onSubmit={submit} className="space-y-3 rounded-lg border border-line p-4">
          <div className="flex flex-wrap gap-3">
            <label className={labelClass}>
              Title
              <input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Movers’ certificate of insurance"
                className={clsx(inputClass, 'w-72')}
              />
            </label>
            <div className={labelClass}>
              Kind
              <FieldPicker
                size="sm"
                className="w-52"
                label="Kind"
                value={draft.kind}
                onChange={(v) => setDraft({ ...draft, kind: v as AmenityDocumentKind })}
                options={(['coi', 'waiver', 'other'] as const).map((k) => ({
                  value: k,
                  label: DOCUMENT_KIND_LABELS[k],
                }))}
              />
            </div>
            <label className={labelClass}>
              Due (days before the booking)
              <input
                inputMode="numeric"
                value={draft.due}
                onChange={(e) => setDraft({ ...draft, due: e.target.value.replace(/\D/g, '') })}
                className={clsx(inputClass, 'w-24')}
              />
            </label>
          </div>
          <label className={labelClass}>
            What households are told
            <textarea
              rows={2}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Must name the association as additional insured, with at least $1M general liability."
              className={clsx(inputClass, 'resize-y')}
            />
          </label>
          <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
            <input
              type="checkbox"
              className={checkboxClass}
              checked={draft.requiresExpiry}
              onChange={(e) => setDraft({ ...draft, requiresExpiry: e.target.checked })}
            />
            The document has an expiry date that must cover the booking
          </label>
          <div className="flex gap-2">
            <Button color="sky" type="submit" disabled={!valid || saving}>
              {saving ? 'Saving…' : editing === 'new' ? 'Add requirement' : 'Save'}
            </Button>
            <Button plain onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {active.length === 0 ? (
        <p className="text-[13px] text-fg-muted">No documents required.</p>
      ) : (
        <RequirementList list={active} readOnly={readOnly} onEdit={(r) => {
          setDraft(requirementDraft(r));
          setEditing(r.id);
        }} onToggle={(r) => setActive(r, false)} toggleLabel="Retire" />
      )}
      {retired.length > 0 && (
        <details className="text-[13px]">
          <summary className="cursor-pointer text-fg-muted">Retired ({retired.length})</summary>
          <div className="mt-2">
            <RequirementList list={retired} readOnly={readOnly} onToggle={(r) => setActive(r, true)} toggleLabel="Restore" />
          </div>
        </details>
      )}
    </div>
  );
};

const RequirementList: React.FC<{
  list: AmenityDocumentRequirementReturn[];
  readOnly: boolean;
  onEdit?: (r: AmenityDocumentRequirementReturn) => void;
  onToggle: (r: AmenityDocumentRequirementReturn) => void;
  toggleLabel: string;
}> = ({ list, readOnly, onEdit, onToggle, toggleLabel }) => (
  <ul className="divide-y divide-line/60 rounded-lg border border-line">
    {list.map((r) => (
      <li key={r.id} className="flex items-center gap-3 px-3 py-2 text-[13px]">
        <span className="min-w-0 flex-1">
          <span className="block font-medium text-fg">{r.title}</span>
          <span className="block text-[12px] text-fg-muted">
            {DOCUMENT_KIND_LABELS[r.kind]} · due {r.due_days_before_start} day{r.due_days_before_start === 1 ? '' : 's'} before
            {r.requires_expiry && ' · must not expire before the booking'}
          </span>
        </span>
        {!readOnly && (
          <>
            {onEdit && (
              <Button plain onClick={() => onEdit(r)}>
                Edit
              </Button>
            )}
            <Button plain onClick={() => onToggle(r)}>
              {toggleLabel}
            </Button>
          </>
        )}
      </li>
    ))}
  </ul>
);

// ---------------------------------------------------------------------------
// Waitlist
// ---------------------------------------------------------------------------

export const AmenityWaitlist: React.FC<{ amenity: AmenityReturn; readOnly: boolean }> = ({ amenity, readOnly }) => {
  const [entries, setEntries] = useState<ReservationWaitlistReturn[]>([]);
  const [showClosed, setShowClosed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await amenityExtrasService.listAmenityWaitlist({ amenityId: amenity.id, includeClosed: showClosed });
      setEntries(res.data.entries);
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't load the waitlist."));
    }
  }, [amenity.id, showClosed]);

  useEffect(() => {
    load();
  }, [load]);

  const withdraw = async (e: ReservationWaitlistReturn) => {
    if (!window.confirm(`Take ${e.home_address} off the waitlist? Any slot offered to them passes to the next household.`)) return;
    try {
      await reservationOperationsService.withdrawWaitlistEntry({ entryId: e.id });
      await load();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't withdraw the entry."));
    }
  };

  const thisYear = new Date().getFullYear();

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className={clsx(helpClass, 'mr-auto')}>
          Households wait for a window on this amenity. When a slot frees, the oldest entry it fits is offered it
          and the slot is held for them for the association’s offer period; declining or letting it lapse passes it on.
        </p>
        <label className="flex items-center gap-2 text-[13px] text-fg-secondary">
          <input
            type="checkbox"
            className={checkboxClass}
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
          />
          Show closed
        </label>
      </div>
      <ErrorAlert>{error}</ErrorAlert>
      {entries.length === 0 ? (
        <p className="text-[13px] text-fg-muted">No one is waiting.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line">
          <table className="w-full text-[13px]">
            <thead className="border-b border-line text-left text-[12px] text-fg-muted">
              <tr>
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Home</th>
                <th className="px-3 py-2 font-medium">Wants</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line/50">
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="px-3 py-2 tabular-nums text-fg-muted">{e.position ?? '—'}</td>
                  <td className="px-3 py-2 text-fg">{e.home_address}</td>
                  <td className="px-3 py-2 text-fg-secondary">
                    {formatDayLabel(e.local_date, thisYear)}, {formatLocalTime(e.earliest_start_local)} –{' '}
                    {formatLocalTime(e.latest_end_local)}
                    <span className="text-fg-muted"> · {e.duration_minutes} min · {e.party_size} {e.party_size === 1 ? 'person' : 'people'}</span>
                  </td>
                  <td className="px-3 py-2">
                    {WAITLIST_STATUS_LABELS[e.status]}
                    {e.status === 'offered' && e.offered_start_local && (
                      <span className="block text-[12px] text-fg-muted">
                        {formatLocalRange(e.offered_start_local, e.offered_end_local ?? e.offered_start_local, thisYear)}
                        {e.offer_expires_at && ` · until ${formatMoment(e.offer_expires_at)}`}
                      </span>
                    )}
                    {offerPriceText(e) && (
                      <span className="block text-[12px] tabular-nums text-fg-secondary">{offerPriceText(e)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!readOnly && waitlistIsLive(e.status) && (
                      <Button plain onClick={() => withdraw(e)}>
                        Withdraw
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className={helpClass}>Times in {amenity.timezone}.</p>
    </div>
  );
};

/** One-line summary of an amenity's phase-3 money settings, for its header. */
export const phaseThreeHeaderBits = (a: AmenityReturn): string[] => {
  const bits: string[] = [];
  if (a.deposit_amount && !isZeroMoney(a.deposit_amount)) bits.push(`${formatMoney(a.deposit_amount)} deposit`);
  if (a.price_rules.length > 0) bits.push(`${a.price_rules.length} pricing rule${a.price_rules.length === 1 ? '' : 's'}`);
  if (a.group_name) bits.push(`In ${a.group_name}`);
  return bits;
};
